<!-- @migration-task Error while migrating Svelte code: This migration would change the name of a slot making the component unusable -->
<script lang="ts">
	import { createSelect, melt, type SelectOption, type SelectOptionProps } from '@melt-ui/svelte';
	import { ChevronDown, ChevronUp } from 'lucide-svelte';
	import { createEventDispatcher, type Snippet } from 'svelte';
	import { fly } from 'svelte/transition';

	type T = $$Generic;

	interface Props {
		label?: string;
		defaultSelected?: SelectOption<T>;
		selectedValue?: T;
		options: SelectOptionProps<T>[];
		placeholder?: string;
		ids?: Partial<Record<'trigger' | 'menu' | 'label', string>>;
		disabled?: boolean;
		errors?: string[];
		class?: string;
		selectedChildren?: Snippet<[string, T | undefined]>;
		optionChildren?: Snippet<[string | undefined, T, boolean]>;
	}

	let {
		label: labelText = '',
		defaultSelected,
		selectedValue = $bindable(),
		options,
		placeholder = '',
		ids = {},
		disabled = false,
		errors = [],
		class: clazz = '',
		selectedChildren,
		optionChildren
	}: Props = $props();

	const dispatch = createEventDispatcher();

	const {
		elements: { trigger, menu, option, label },
		states: { open, selectedLabel, selected },
		helpers: { isSelected }
	} = createSelect<T>({
		disabled,
		defaultSelected,
		onSelectedChange: ({ next }) => {
			dispatch('change', { next });
			return next;
		},
		ids
	});

	$effect(() => {
		if ($selected) selectedValue = $selected.value;
	});
</script>

<div class="{errors?.length ? 'error' : ''} {clazz}">
	{#if labelText}
		<!-- svelte-ignore a11y_label_has_associated_control -->
		<label use:melt={$label} class="mb-3 block">
			{#if disabled}
				<span class="body-bold text-gray-mid">{labelText}</span>
			{:else}
				<span class="body-bold">{labelText}</span>
			{/if}
		</label>
	{/if}

	<button
		use:melt={$trigger}
		{disabled}
		class="group body-bold border-gray-light bg-white-pure disabled:text-gray-mid relative flex h-12 w-full items-center justify-between rounded-xl border-1 px-4 pr-12 overflow-ellipsis"
	>
		{#if $selectedLabel.length}
			{#if selectedChildren}
				{@render selectedChildren($selectedLabel, selectedValue)}
			{:else}
				{$selectedLabel}
			{/if}
		{:else}
			<span class="body-regular text-gray-mid">{placeholder}</span>
		{/if}
		<div class="absolute top-1/2 right-4 z-10 flex -translate-y-1/2 text-black">
			{#if $open}
				<ChevronUp class="w-6" />
			{:else}
				<ChevronDown class="w-6" />
			{/if}
		</div>
	</button>

	{#each errors ?? [] as message}
		<p class="message mt-2 text-sm">{message}</p>
	{/each}
</div>
{#if $open}
	<ul
		class="bg-white-pure z-20 flex max-h-[300px] flex-col overflow-hidden rounded-2xl shadow-md"
		use:melt={$menu}
		transition:fly={{ duration: 150, y: -5 }}
	>
		<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
		<div class="flex max-h-full flex-col gap-0 overflow-y-auto text-black" tabindex="0">
			{#each options as o}
				<li
					use:melt={$option(o)}
					class="data-[selected]:bg-green-primary data-[selected]:text-white-pure relative cursor-pointer scroll-my-2 py-2 pr-4 pl-4 data-[highlighted]:bg-[#045040]/[0.07]"
				>
					{#if optionChildren}
						{@render optionChildren(o.label, o.value, $isSelected(o.value))}
					{:else}
						<div class="pl-4">{o.label}</div>
					{/if}
				</li>
			{/each}
		</div>
	</ul>
{/if}

<style lang="postcss">
	@reference '../app.css';

	.error {
		.message {
			@apply text-red-ticker;
		}

		button {
			@apply border-red-ticker border-2;
		}
	}
</style>
